/**
 * EmbeddedDistrictMap - Compact map for organization profile page
 * Shows all districts with the selected one highlighted
 * Shows school points for the selected district when zoomed in
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MapContainer, TileLayer, GeoJSON, useMap, useMapEvents } from 'react-leaflet';
import { useNavigate } from 'react-router-dom';
import type { Layer, PathOptions, LeafletMouseEvent } from 'leaflet';
import L from 'leaflet';
import type { Feature, Geometry } from 'geojson';
import { Spinner } from 'flowbite-react';
import polylabel from 'polylabel';
import { mapApi, type DistrictPolygonsResponse, type DistrictPolygonProperties } from '../../api';
import type { SchoolPointsResponse } from '../../api/map';
import 'leaflet/dist/leaflet.css';

// Hook to detect dark mode from Flowbite/Tailwind
const useDarkMode = () => {
  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains('dark')
  );

  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          setIsDark(document.documentElement.classList.contains('dark'));
        }
      });
    });

    observer.observe(document.documentElement, { attributes: true });
    return () => observer.disconnect();
  }, []);

  return isDark;
};

// AIU Brand Colors - Light mode
const COLORS_LIGHT = {
  primary: '#004C97',      // Primary Blue - default fill
  light: '#009CDE',        // Light Blue - hover
  dark: '#003865',         // Dark Blue - borders
  teal: '#008C95',         // Teal
  red: '#A6192E',          // AIU Red (PMS 187) - selected/highlighted
  green: '#00843D',        // Green
  gray: '#C1C6C8',         // Gray
  labelText: '#003865',    // District label text (selected)
  labelTextMuted: '#999',  // District label text (non-selected)
  labelShadow: 'white',    // Label shadow
  schoolLabelText: '#00843D',
  markerBg: 'white',
};

// AIU Brand Colors - Dark mode
const COLORS_DARK = {
  primary: '#3B82F6',      // Brighter blue for dark backgrounds
  light: '#60A5FA',        // Light Blue - hover
  dark: '#93C5FD',         // Light border for contrast
  teal: '#14B8A6',         // Brighter teal
  red: '#EF4444',          // Brighter red for selected
  green: '#22C55E',        // Brighter green
  gray: '#9CA3AF',         // Gray
  labelText: '#E5E7EB',    // Light text for selected
  labelTextMuted: '#9CA3AF', // Muted text for non-selected
  labelShadow: '#1F2937',  // Dark shadow
  schoolLabelText: '#4ADE80',
  markerBg: '#374151',
};

// Tile layer URLs
// CARTO watermarks unauthenticated raster requests with "API KEY REQUIRED";
// the key is free (5M tiles/month): https://carto.com/basemaps/apikey/
const CARTO_KEY = import.meta.env.VITE_CARTO_API_KEY;
const cartoTileUrl = (style: string) =>
  `https://basemaps.cartocdn.com/rastertiles/${style}/{z}/{x}/{y}{r}.png${CARTO_KEY ? `?key=${CARTO_KEY}` : ''}`;

const TILE_URLS = {
  light: {
    base: cartoTileUrl('light_nolabels'),
    labels: cartoTileUrl('light_only_labels'),
  },
  dark: {
    base: cartoTileUrl('dark_nolabels'),
    labels: cartoTileUrl('dark_only_labels'),
  },
};

// Pittsburgh area center (fallback)
const DEFAULT_CENTER: [number, number] = [40.44, -79.99];
const DEFAULT_ZOOM = 10;

// Zoom thresholds
const DISTRICT_LABEL_ZOOM_THRESHOLD = 11;
const SCHOOL_ZOOM_THRESHOLD = 10;
const SCHOOL_LABEL_ZOOM_THRESHOLD = 12;
const BASEMAP_LABELS_ZOOM_THRESHOLD = 13;

interface EmbeddedDistrictMapProps {
  selectedOrgId: number;
  selectedLeaId?: number;
  height?: string;               // Fixed height (default behavior)
  minHeight?: string;            // Minimum height when using flex container
  fillContainer?: boolean;       // If true, uses height: 100% to fill parent container
  highlightedOrgIds?: number[];  // Highlight multiple districts (for CTCs)
  schoolLeaIds?: number[];       // Show schools for multiple LEAs
  refreshKey?: number;           // Change to trigger data refetch (e.g. after address save)
}

// Component to fit bounds to selected district(s) with more context
const FitBoundsToSelected: React.FC<{
  polygons: DistrictPolygonsResponse;
  selectedOrgId: number;
  highlightedOrgIds?: number[];
}> = ({ polygons, selectedOrgId, highlightedOrgIds }) => {
  const map = useMap();

  useEffect(() => {
    const targetIds = highlightedOrgIds && highlightedOrgIds.length > 0
      ? highlightedOrgIds
      : [selectedOrgId];

    const targetFeatures = polygons.features.filter(
      f => targetIds.includes(f.properties.educationOrganizationId)
    );

    if (targetFeatures.length > 0) {
      const group = L.featureGroup(targetFeatures.map(f => L.geoJSON(f)));
      const bounds = group.getBounds();

      if (bounds.isValid()) {
        map.fitBounds(bounds, { paddingTopLeft: [15, 0], paddingBottomRight: [15, 60], maxZoom: 11 });
      }
    }
  }, [map, polygons, selectedOrgId, highlightedOrgIds]);

  return null;
};

// Track zoom level
const ZoomTracker: React.FC<{ onZoomChange: (zoom: number) => void }> = ({ onZoomChange }) => {
  const map = useMapEvents({
    zoomend: () => {
      onZoomChange(map.getZoom());
    },
  });

  useEffect(() => {
    onZoomChange(map.getZoom());
  }, [map, onZoomChange]);

  return null;
};

// Compute optimal label point using pole of inaccessibility (polylabel)
// Returns the point inside the polygon farthest from any edge -- ideal for labels
function getLabelPoint(geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon): [number, number] {
  // For MultiPolygon, use the largest polygon (most area)
  let coords: number[][][];
  if (geometry.type === 'MultiPolygon') {
    // Pick the polygon with the most coordinates as a proxy for largest
    let largest = geometry.coordinates[0];
    for (const poly of geometry.coordinates) {
      if (poly[0].length > largest[0].length) {
        largest = poly;
      }
    }
    coords = largest;
  } else {
    coords = geometry.coordinates;
  }

  const result = polylabel(coords, 0.001);
  // polylabel returns [lng, lat], we need [lat, lng] for Leaflet
  return [result[1], result[0]];
}

// District name labels at polygon centroids
const DistrictLabels: React.FC<{
  polygons: DistrictPolygonsResponse;
  selectedOrgId: number;
  visible: boolean;
  colors: typeof COLORS_LIGHT;
}> = ({ polygons, selectedOrgId, visible, colors }) => {
  const map = useMap();

  useEffect(() => {
    const markers: L.Marker[] = [];

    polygons.features.forEach((feature) => {
      const isSelected = feature.properties.educationOrganizationId === selectedOrgId;

      // Non-selected labels only show when zoomed in
      if (!isSelected && !visible) return;

      const centroid = getLabelPoint(feature.geometry as GeoJSON.Polygon | GeoJSON.MultiPolygon);
      const name = feature.properties.shortName || feature.properties.name;

      const icon = L.divIcon({
        className: 'district-label',
        html: `<span style="
          display: block;
          max-width: 80px;
          font-size: 10px;
          font-weight: ${isSelected ? '700' : '500'};
          line-height: 1.2;
          color: ${isSelected ? colors.labelText : colors.labelTextMuted};
          text-shadow: 1px 1px 2px ${colors.labelShadow}, -1px -1px 2px ${colors.labelShadow}, 1px -1px 2px ${colors.labelShadow}, -1px 1px 2px ${colors.labelShadow};
          text-align: center;
          pointer-events: none;
        ">${name}</span>`,
        iconSize: [80, 0],
        iconAnchor: [40, 0],
      });

      const marker = L.marker(centroid, { icon, interactive: false, zIndexOffset: 1000 }).addTo(map);
      markers.push(marker);
    });

    return () => {
      markers.forEach((m) => m.remove());
    };
  }, [map, polygons, selectedOrgId, visible, colors]);

  return null;
};

// School markers for the selected district(s)
const SelectedDistrictSchools: React.FC<{
  schools: SchoolPointsResponse | null;
  selectedLeaId?: number;
  schoolLeaIds?: number[];
  visible: boolean;
  showLabels: boolean;
  colors: typeof COLORS_LIGHT;
}> = ({ schools, selectedLeaId, schoolLeaIds, visible, showLabels, colors }) => {
  const map = useMap();

  useEffect(() => {
    if (!visible || !schools) return;

    const leaIds = schoolLeaIds && schoolLeaIds.length > 0
      ? schoolLeaIds
      : selectedLeaId ? [selectedLeaId] : [];

    if (leaIds.length === 0) return;

    const layers: L.Layer[] = [];

    // Create SVG with current colors
    const schoolIconSvg = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="14" height="14">
  <path fill="${colors.green}" stroke="${colors.markerBg}" stroke-width="1.5"
    d="M12 3L1 9l4 2.18v6L12 21l7-3.82v-6l2-1.09V17h2V9L12 3z
       M18.82 9L12 12.72 5.18 9 12 5.28 18.82 9z"/>
</svg>`.trim();

    // Filter to schools belonging to the target LEA(s)
    const districtSchools = schools.features.filter(
      f => leaIds.includes(f.properties.leaId!)
    );

    districtSchools.forEach((feature) => {
      const [lng, lat] = feature.geometry.coordinates;
      const props = feature.properties;

      const iconHtml = `<div style="
        display: flex;
        align-items: center;
        justify-content: center;
        width: 18px;
        height: 18px;
        background: ${colors.markerBg};
        border-radius: 50%;
        box-shadow: 0 1px 3px rgba(0,0,0,0.3);
      ">${schoolIconSvg}</div>`;

      const icon = L.divIcon({
        className: 'school-marker',
        html: iconHtml,
        iconSize: [18, 18],
        iconAnchor: [9, 9],
      });

      const tooltipContent = `
        <div style="font-family: system-ui, sans-serif;">
          <strong style="font-size: 11px;">${props.name}</strong>
          ${props.schoolType ? `<br/><span style="color: #666; font-size: 10px;">${props.schoolType}</span>` : ''}
        </div>
      `;

      const marker = L.marker([lat, lng], { icon })
        .bindTooltip(tooltipContent, { direction: 'top', offset: [0, -10] })
        .addTo(map);

      layers.push(marker);

      // Permanent school name label
      if (showLabels) {
        const labelIcon = L.divIcon({
          className: 'school-label',
          html: `<span style="
            display: block;
            max-width: 90px;
            font-size: 9px;
            font-weight: 500;
            line-height: 1.2;
            color: ${colors.schoolLabelText};
            text-shadow: 1px 1px 2px ${colors.labelShadow}, -1px -1px 2px ${colors.labelShadow}, 1px -1px 2px ${colors.labelShadow}, -1px 1px 2px ${colors.labelShadow};
            text-align: center;
            pointer-events: none;
          ">${props.name}</span>`,
          iconSize: [90, 0],
          iconAnchor: [45, -12],
        });

        const label = L.marker([lat, lng], { icon: labelIcon, interactive: false })
          .addTo(map);

        layers.push(label);
      }
    });

    return () => {
      layers.forEach((l) => l.remove());
    };
  }, [map, schools, selectedLeaId, schoolLeaIds, visible, showLabels, colors]);

  return null;
};

export const EmbeddedDistrictMap: React.FC<EmbeddedDistrictMapProps> = ({
  selectedOrgId,
  selectedLeaId,
  height = '200px',
  minHeight,
  fillContainer = false,
  highlightedOrgIds,
  schoolLeaIds,
  refreshKey,
}) => {
  // Compute container style based on props
  const containerStyle: React.CSSProperties = fillContainer
    ? { height: '100%', minHeight: minHeight || height, width: '100%', overflow: 'hidden', borderRadius: '0.25rem' }
    : { height, minHeight, width: '100%', overflow: 'hidden', borderRadius: '0.25rem' };
  const navigate = useNavigate();
  const isDarkMode = useDarkMode();
  const [polygons, setPolygons] = useState<DistrictPolygonsResponse | null>(null);
  const [schools, setSchools] = useState<SchoolPointsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hoveredOrgId, setHoveredOrgId] = useState<number | null>(null);
  const [currentZoom, setCurrentZoom] = useState(DEFAULT_ZOOM);

  // Select colors based on dark mode
  const colors = isDarkMode ? COLORS_DARK : COLORS_LIGHT;
  const tileUrls = isDarkMode ? TILE_URLS.dark : TILE_URLS.light;

  // Fetch polygon and school data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const [polygonData, schoolData] = await Promise.all([
          mapApi.getPolygons(),
          mapApi.getSchools().catch(() => null),
        ]);
        setPolygons(polygonData);
        setSchools(schoolData);
      } catch (err) {
        console.error('Failed to fetch map data:', err);
        setError('Failed to load map data.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [refreshKey]);

  // Handle click navigation
  const handleClick = useCallback((leaId: number, orgId: number) => {
    if (orgId !== selectedOrgId) {
      navigate(`/leas/${leaId}`);
    }
  }, [navigate, selectedOrgId]);

  // Style function for polygons - highlight selected/highlighted districts
  const getStyle = useCallback((feature?: Feature<Geometry, DistrictPolygonProperties>): PathOptions => {
    const orgId = feature?.properties?.educationOrganizationId;
    const isSelected = orgId === selectedOrgId;
    const isHighlighted = highlightedOrgIds && highlightedOrgIds.length > 0
      ? highlightedOrgIds.includes(orgId!)
      : isSelected;
    const isHovered = orgId === hoveredOrgId && !isHighlighted;

    if (isHighlighted) {
      return {
        fillColor: colors.red,
        fillOpacity: 0.5,
        color: colors.dark,
        weight: 3,
      };
    }

    return {
      fillColor: isHovered ? colors.light : colors.primary,
      fillOpacity: isHovered ? 0.5 : 0.3,
      color: colors.dark,
      weight: isHovered ? 2 : 1,
    };
  }, [selectedOrgId, hoveredOrgId, highlightedOrgIds, colors]);

  // Event handlers for each feature
  const onEachFeature = useCallback((
    feature: Feature<Geometry, DistrictPolygonProperties>,
    layer: Layer
  ) => {
    const props = feature.properties;
    const isSelected = props.educationOrganizationId === selectedOrgId;

    layer.on({
      mouseover: (e: LeafletMouseEvent) => {
        setHoveredOrgId(props.educationOrganizationId);
        if (!isSelected) {
          e.target.bringToFront();
        }
      },
      mouseout: () => {
        setHoveredOrgId(null);
      },
      click: () => {
        handleClick(props.leaId, props.educationOrganizationId);
      },
    });

    const tooltipContent = isSelected
      ? `<strong>${props.name}</strong>`
      : `<strong>${props.name}</strong><br/><span style="font-size: 11px; color: #666;">Click to view</span>`;

    layer.bindTooltip(tooltipContent, {
      sticky: true,
      direction: 'top',
      offset: [0, -5],
    });
  }, [selectedOrgId, handleClick]);

  // Create a unique key for GeoJSON to force re-render when hover/highlight/dark mode state changes
  const geoJsonKey = useMemo(
    () => `embedded-geojson-${selectedOrgId}-${hoveredOrgId || 'none'}-${highlightedOrgIds?.join(',') || 'none'}-${isDarkMode ? 'dark' : 'light'}`,
    [selectedOrgId, hoveredOrgId, highlightedOrgIds, isDarkMode]
  );

  const showDistrictLabels = currentZoom >= DISTRICT_LABEL_ZOOM_THRESHOLD;
  const showSchools = currentZoom >= SCHOOL_ZOOM_THRESHOLD;
  const showSchoolLabels = currentZoom >= SCHOOL_LABEL_ZOOM_THRESHOLD;
  const showBasemapLabels = currentZoom >= BASEMAP_LABELS_ZOOM_THRESHOLD;

  if (loading) {
    return (
      <div
        className="flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded"
        style={containerStyle}
      >
        <Spinner size="md" />
      </div>
    );
  }

  if (error || !polygons || polygons.features.length === 0) {
    return null; // Silently hide if no data
  }

  return (
    <div style={containerStyle}>
    <MapContainer
      center={DEFAULT_CENTER}
      zoom={DEFAULT_ZOOM}
      style={{ height: '100%', width: '100%' }}
      className="rounded"
      scrollWheelZoom={true}
      zoomControl={true}
      key={isDarkMode ? 'dark' : 'light'} // Force re-mount when dark mode changes
    >
      <TileLayer
        url={tileUrls.base}
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
      />
      <ZoomTracker onZoomChange={setCurrentZoom} />
      <GeoJSON
        key={geoJsonKey}
        data={polygons}
        style={getStyle}
        onEachFeature={onEachFeature}
      />
      {showBasemapLabels && (
        <TileLayer
          url={tileUrls.labels}
          zIndex={650}
        />
      )}
      <FitBoundsToSelected polygons={polygons} selectedOrgId={selectedOrgId} highlightedOrgIds={highlightedOrgIds} />
      <DistrictLabels polygons={polygons} selectedOrgId={selectedOrgId} visible={showDistrictLabels} colors={colors} />
      <SelectedDistrictSchools
        schools={schools}
        selectedLeaId={selectedLeaId}
        schoolLeaIds={schoolLeaIds}
        visible={showSchools}
        showLabels={showSchoolLabels}
        colors={colors}
      />
    </MapContainer>
    </div>
  );
};

export default EmbeddedDistrictMap;
